/**
 * How long a seller has left to accept an order.
 *
 * An unaccepted order expires and refunds the customer, and until recently no
 * screen said so — a seller could lose the sale to a timeout without ever being
 * told a clock was running. The window is the shop's `orderAcceptTimeout`,
 * already fetched with the seller, counted from when the order was placed.
 *
 * One helper because two screens state it — the order page and the triage
 * queue — and they must not drift into saying different things about the same
 * order.
 *
 * It returns the parts rather than a finished sentence: the queue row says
 * "expires in 9 hours" and the order page says "Respond within 9 hours", and an
 * expired order has to read differently again. A first version returned one
 * string for both and produced "expires in expired".
 */
export interface AcceptWindow {
  /** The deadline has passed. */
  expired: boolean;
  /** "9 hours", "2 days" — absent when there is no window configured. */
  duration?: string;
}

export const acceptWindow = (
  orderDate: string | Date,
  windowHours: number,
  now: number = Date.now(),
): AcceptWindow | undefined => {
  if (!windowHours || windowHours <= 0) return undefined;
  const placed = new Date(orderDate).getTime();
  if (Number.isNaN(placed)) return undefined;

  const msLeft = placed + windowHours * 3600_000 - now;
  if (msLeft <= 0) return { expired: true };

  const hours = Math.ceil(msLeft / 3600_000);
  // Past two days, hours stop being the useful unit — "expires in 54 hours" is
  // arithmetic the reader has to do.
  if (hours >= 48) {
    const days = Math.round(hours / 24);
    return { expired: false, duration: `${days} ${days === 1 ? 'day' : 'days'}` };
  }
  return { expired: false, duration: `${hours} ${hours === 1 ? 'hour' : 'hours'}` };
};

/** "expires in 9 hours" / "expired" — the queue row's and subtitle's phrasing. */
export const expiryClause = (
  orderDate: string | Date,
  windowHours: number,
  now: number = Date.now(),
): string | undefined => {
  const w = acceptWindow(orderDate, windowHours, now);
  if (!w) return undefined;
  return w.expired ? 'expired' : `expires in ${w.duration}`;
};
